import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Tpl3plService, Tpl3plConfig } from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { InventoryService } from '../../../services/inventory.service';
import { OrdersService } from '../../../services/orders.service';
import { GlobalsService } from '../../../services/globals.service';

@Component({
	selector: 'app-tpl3pl-config-form',
	templateUrl: './tpl3pl-config-form.component.html',
	styleUrls: ['./tpl3pl-config-form.component.scss']
})
export class Tpl3plConfigFormComponent implements OnInit {
	configForm: UntypedFormGroup;
	config: Tpl3plConfig | null = null;
	loading = false;
	saving = false;
	testing = false;
	testResult: any = null;
	categories: any[] = [];
	productlines: any[] = [];
	brands: any[] = [];
	orderStatuses: any[] = [];
	paymentTerms: Array<{ termsindicator: string; terms: string; cc_process?: any }> = [];

	constructor(
		private fb: UntypedFormBuilder,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private inventoryService: InventoryService,
		private ordersService: OrdersService,
		private globalsService: GlobalsService
	) {
		this.configForm = this.fb.group({
			enabled: [false],
			integration_type: ['API', Validators.required],
			warehouse_code: ['00', Validators.required],
			test_mode: [true],
			config_json: this.fb.group({
				api: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					apiKey: [''],
					apiSecret: [''],
					timeout: [30, [Validators.min(1)]],
					retryAttempts: [3, [Validators.min(0)]]
				}),
				pgs: this.fb.group({
					baseUrl: ['', [this.urlValidator()]],
					tokenUrl: ['', [this.urlValidator()]],
					userId: [''],
					passwd: [''],
					defaultWarehouse: ['CM'],
					defaultOutboundChannel: ['B2C'],
					b2cServiceType: ['Ground'],
					inventoryMaxResults: [500, [Validators.min(1), Validators.max(2000)]],
					inventoryLoccode: [''],
					warehouseMapJson: [''],
					// Read by Tpl3plCreditReceiveService: auto-post the AR credit when PGS
					// confirms a customer return. It had no control here, so every Save
					// dropped it from config_json and returns silently stopped crediting.
					auto_credit_on_receipt: [false],
					lob: this.fb.group({
						default: [''],
						byManufacturerIdJson: [''],
						byManufacturerNameJson: ['']
					})
				}),
				edi: this.fb.group({
					tradingPartnerId: [''],
					isaQualifier: ['ZZ'],
					gsQualifier: ['01']
				}),
				sftp: this.fb.group({
					host: [''],
					port: [22, [this.portValidator()]],
					username: [''],
					password: [''],
					remotePath: ['/inbound'],
					outboundPath: ['/outbound']
				}),
				mapping: this.fb.group({
					orderStatus: this.fb.group({}),
					carrierMapping: this.fb.group({}),
					b2bB2c: this.fb.group({})
				}),
				allowed_statuses: [[]],
				blocked_statuses: [[]],
				shipped_statuses: [[]],
				auto_invoice_on_shipment: [false],
				email_customer_on_3pl_invoice: [false],
				status_on_export_success: [''],
				status_on_shipment_received: [''],
				status_on_tracking_in_transit: [''],
				status_on_tracking_delivered: [''],
				status_on_invoice_failed: [''],
				status_on_shipment_approved: [''],
				sku_categories: [[]],
				sku_lines: [[]],
				sku_manufacturers: [[]],
				skip_gl_for_3pl_inventory: [false],
				split_partial_backorder_enabled: [false],
				split_uninvoiced_open_lines_enabled: [false],
				auto_split_partial_before_export: [false],
				skip_not_fillable_orders: [false],
				not_fillable_hold_minutes: [60, [Validators.min(1)]],
				backorder_orderstatusid: [''],
				queue_backorder_for_tpl3pl: [false],
				hold_for_payment_orderstatusid: [''],
				must_pay_payterms: [[]],
				net_payterms: [[]],
				pgs_sync_erp_status_rank_json: [''],
				pgs_sync_erp_skip_when_orderstatusid: [[]],
				shipment_invoice_worker_enabled: [false],
				worker_stage2_enabled: [false],
				worker_stage3_enabled: [false],
				worker_auto_attach_card: [true],
				worker_max_per_run: [100, [Validators.min(1)]],
				worker_stage2_sleep_ms: [500, [Validators.min(0)]],
				worker_stage3_min_ship_age_days: [0, [Validators.min(0)]],
				payment_usage: this.fb.group({
					apply_customer_ar_on_invoice: [false],
					ar_auto_apply_types_text: ['11'],
					allow_manual_open_payment_application: [true],
					credit_branch_scope: ['branch'],
					credit_max_auto_apply_amount: ['']
				})
			})
		});
	}

	pgsKeywordRules: Array<{keyword: string, statusId: number | string}> = [];
	uniquePgsStatuses: Array<{status: string, count: number, mappedStatusId: number | string}> = [];
	loadingPgsStatuses = false;

	compareByManufacturerId = (a: any, b: any): boolean => Number(a) === Number(b);
	
	compareByStatusId = (a: any, b: any): boolean => {
		if (a === '' || b === '' || a == null || b == null) {
			return a === b;
		}
		return Number(a) === Number(b);
	};

	comparePayterms = (a: any, b: any): boolean => {
		if (a == null || b == null) {
			return a === b;
		}
		return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
	};

	get paymentTermsForSelect(): Array<{ termsindicator: string; terms: string; cc_process?: any }> {
		return [...this.paymentTerms]
			.filter((t) => t && String(t.termsindicator || '').trim() !== '')
			.sort((a, b) => String(a.terms || a.termsindicator).localeCompare(String(b.terms || b.termsindicator)));
	}

	private loadPgsKeywordRules(cj: any): void {
		this.pgsKeywordRules = [];
		if (Array.isArray(cj.pgs_sync_erp_keyword_status_ids)) {
			for (const rule of cj.pgs_sync_erp_keyword_status_ids) {
				if (rule && typeof rule === 'object' && rule.order_status_id) {
					const keywords = rule.match || rule.keywords || rule.keyword || '';
					const keywordStr = Array.isArray(keywords) ? keywords.join(', ') : String(keywords);
					const statusId = Number(rule.order_status_id);
					if (keywordStr && !isNaN(statusId)) {
						this.pgsKeywordRules.push({ keyword: keywordStr, statusId });
					}
				}
			}
		}
		if (this.pgsKeywordRules.length === 0) {
			this.addPgsKeywordRule();
		}
	}

	addPgsKeywordRule(): void {
		this.pgsKeywordRules.push({ keyword: '', statusId: '' });
	}

	removePgsKeywordRule(index: number): void {
		this.pgsKeywordRules.splice(index, 1);
		if (this.pgsKeywordRules.length === 0) {
			this.addPgsKeywordRule();
		}
	}

	loadUniquePgsStatuses(): void {
		this.loadingPgsStatuses = true;
		this.tpl3plService.getUniquePgsStatuses().subscribe({
			next: (response) => {
				if (response.success && Array.isArray(response.statuses)) {
					this.uniquePgsStatuses = response.statuses.map((s: any) => ({
						status: s.status,
						count: s.count,
						mappedStatusId: this.findMappedStatusForPgsStatus(s.status)
					}));
				}
				this.loadingPgsStatuses = false;
			},
			error: () => {
				this.loadingPgsStatuses = false;
			}
		});
	}

	private findMappedStatusForPgsStatus(pgsStatus: string): string {
		const pgsLower = pgsStatus.toLowerCase();
		const cj = this.config?.config_json || {};
		
		if (Array.isArray(cj.pgs_sync_erp_keyword_status_ids)) {
			for (const rule of cj.pgs_sync_erp_keyword_status_ids) {
				if (!rule || !rule.order_status_id) continue;
				
				const keywords = rule.match || rule.keywords || rule.keyword || '';
				const keywordArray = Array.isArray(keywords) ? keywords : [keywords];
				
				for (const kw of keywordArray) {
					const kwLower = String(kw).toLowerCase().trim();
					if (kwLower && pgsLower.includes(kwLower)) {
						return String(rule.order_status_id);
					}
				}
			}
		}
		
		return '';
	}

	/** Sales order status IDs for API config; accepts saved arrays or legacy comma-separated strings. */
	private normalizeOrderStatusIds(value: unknown): number[] {
		if (Array.isArray(value)) {
			return value.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
		}
		if (typeof value === 'string') {
			return (value || '')
				.split(/[\s,]+/)
				.map((x: string) => parseInt(x.trim(), 10))
				.filter((n: number) => !isNaN(n));
		}
		return [];
	}

	get orderStatusesForSelect(): any[] {
		return [...this.orderStatuses]
			.filter((s) => s && !isNaN(Number(s.order_status_id)))
			.sort((a, b) => {
				const na = Number(a.order_status_id);
				const nb = Number(b.order_status_id);
				if (na !== nb) {
					return na - nb;
				}
				return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
			});
	}

	private normalizeToArray(value: any): any[] {
		if (Array.isArray(value)) {
			return value;
		}
		if (!value || typeof value !== 'object') {
			return [];
		}
		if (Array.isArray(value.rows)) {
			return value.rows;
		}
		if (Array.isArray(value.data)) {
			return value.data;
		}
		if (Array.isArray(value.results)) {
			return value.results;
		}
		if (Array.isArray(value.items)) {
			return value.items;
		}
		return [];
	}

	ngOnInit(): void {
		this.loadConfig();
		this.inventoryService.getItemCategories().subscribe((c: any) => { this.categories = this.normalizeToArray(c); });
		this.inventoryService.getItemProductlines().subscribe((p: any) => { this.productlines = this.normalizeToArray(p); });
		this.inventoryService.getBrands().subscribe((b: any) => { this.brands = this.normalizeToArray(b); });
		this.ordersService.getOrderStatuses().subscribe((r: any) => { this.orderStatuses = this.normalizeToArray(r); });
		this.globalsService.getTerms().subscribe((r: any) => { this.paymentTerms = this.normalizeToArray(r); });
		this.configForm.get('integration_type')?.valueChanges.subscribe((type) => {
			this.updateIntegrationValidators();
			if (type === 'PGS') {
				this.loadUniquePgsStatuses();
			}
		});
		this.updateIntegrationValidators();
	}

	loadConfig(): void {
		this.loading = true;
		this.tpl3plService.getConfig().subscribe({
			next: (config) => {
				this.config = config;
				const cj = config.config_json || {};
				if (!cj.pgs) { cj.pgs = {}; }
				const pgsForForm = { ...(cj.pgs as object) } as Record<string, unknown>;
				const lobRaw = pgsForForm.lob as Record<string, unknown> | undefined;
				if (lobRaw && typeof lobRaw === 'object' && !Array.isArray(lobRaw)) {
					const by = lobRaw.byManufacturerName;
					const byId = lobRaw.byManufacturerId;
					pgsForForm.lob = {
						default: (lobRaw.default as string) || '',
						byManufacturerIdJson:
							byId && typeof byId === 'object' && !Array.isArray(byId) ? JSON.stringify(byId, null, 2) : '',
						byManufacturerNameJson:
							by && typeof by === 'object' && !Array.isArray(by) ? JSON.stringify(by, null, 2) : ''
					};
				} else {
					pgsForForm.lob = { default: '', byManufacturerIdJson: '', byManufacturerNameJson: '' };
				}
				const wmRaw = pgsForForm.warehouseMap;
				delete pgsForForm.warehouseMap;
				pgsForForm.warehouseMapJson =
					wmRaw && typeof wmRaw === 'object' && !Array.isArray(wmRaw)
						? JSON.stringify(wmRaw, null, 2)
						: '';
				if (pgsForForm.inventoryLoccode === undefined || pgsForForm.inventoryLoccode === null) {
					pgsForForm.inventoryLoccode = '';
				} else {
					pgsForForm.inventoryLoccode = String(pgsForForm.inventoryLoccode);
				}
				pgsForForm.auto_credit_on_receipt = !!pgsForForm.auto_credit_on_receipt;
				const toArray = (a: unknown): unknown[] =>
					Array.isArray(a) ? [...a] : (typeof a === 'string' ? a.split(/[\s,]+/).map(s => s.trim()).filter(Boolean) : []);
				const toNumArray = (a: unknown): number[] =>
					Array.isArray(a) ? a.map((x: any) => Number(x)).filter((n: number) => !isNaN(n)) : [];
				const cjClean = { ...(cj as Record<string, unknown>) };
				delete (cjClean as { payment_usage?: unknown }).payment_usage;
				delete (cjClean as { apply_customer_ar_on_invoice?: unknown }).apply_customer_ar_on_invoice;
				delete (cjClean as { ar_auto_apply_types?: unknown }).ar_auto_apply_types;
				delete (cjClean as { allow_manual_open_payment_application?: unknown }).allow_manual_open_payment_application;
				delete (cjClean as { credit_branch_scope?: unknown }).credit_branch_scope;
				delete (cjClean as { credit_max_auto_apply_amount?: unknown }).credit_max_auto_apply_amount;
				const puDb = (cj as { payment_usage?: Record<string, unknown> }).payment_usage;
				const typesArr = Array.isArray(puDb?.ar_auto_apply_types)
					? (puDb.ar_auto_apply_types as unknown[])
					: Array.isArray(cj.ar_auto_apply_types)
						? cj.ar_auto_apply_types
						: [];
				const arTypesText =
					typesArr.length > 0 ? typesArr.map((x: unknown) => String(x)).join(',') : '11';
				this.configForm.patchValue({
					enabled: config.enabled,
					integration_type: config.integration_type,
					warehouse_code: config.warehouse_code,
					test_mode: config.test_mode,
					config_json: {
						...cjClean,
						pgs: pgsForForm,
						allowed_statuses: this.normalizeOrderStatusIds(cj.allowed_statuses),
						blocked_statuses: this.normalizeOrderStatusIds(cj.blocked_statuses),
						shipped_statuses: this.normalizeOrderStatusIds(cj.shipped_statuses),
						auto_invoice_on_shipment: !!cj.auto_invoice_on_shipment,
						email_customer_on_3pl_invoice: !!cj.email_customer_on_3pl_invoice,
						status_on_export_success: cj.status_on_export_success != null && cj.status_on_export_success !== '' ? Number(cj.status_on_export_success) : '',
						status_on_shipment_received: cj.status_on_shipment_received != null && cj.status_on_shipment_received !== '' ? Number(cj.status_on_shipment_received) : '',
						status_on_tracking_in_transit: cj.status_on_tracking_in_transit != null && cj.status_on_tracking_in_transit !== '' ? Number(cj.status_on_tracking_in_transit) : '',
						status_on_tracking_delivered: cj.status_on_tracking_delivered != null && cj.status_on_tracking_delivered !== '' ? Number(cj.status_on_tracking_delivered) : '',
						status_on_invoice_failed: cj.status_on_invoice_failed != null && cj.status_on_invoice_failed !== '' ? Number(cj.status_on_invoice_failed) : '',
						status_on_shipment_approved: cj.status_on_shipment_approved != null && cj.status_on_shipment_approved !== '' ? Number(cj.status_on_shipment_approved) : '',
						sku_categories: toArray(cj.sku_categories),
						sku_lines: toArray(cj.sku_lines),
						sku_manufacturers: toNumArray(cj.sku_manufacturers),
						skip_gl_for_3pl_inventory: !!cj.skip_gl_for_3pl_inventory,
						split_partial_backorder_enabled: !!cj.split_partial_backorder_enabled,
						split_uninvoiced_open_lines_enabled: !!cj.split_uninvoiced_open_lines_enabled,
						auto_split_partial_before_export: !!cj.auto_split_partial_before_export,
						skip_not_fillable_orders: !!cj.skip_not_fillable_orders,
						not_fillable_hold_minutes:
							cj.not_fillable_hold_minutes != null && String(cj.not_fillable_hold_minutes) !== ''
								? Math.max(1, parseInt(String(cj.not_fillable_hold_minutes), 10) || 60)
								: 60,
						backorder_orderstatusid:
							cj.backorder_orderstatusid != null && cj.backorder_orderstatusid !== ''
								? Number(cj.backorder_orderstatusid)
								: '',
						queue_backorder_for_tpl3pl: !!cj.queue_backorder_for_tpl3pl,
						hold_for_payment_orderstatusid:
							cj.hold_for_payment_orderstatusid != null && cj.hold_for_payment_orderstatusid !== ''
								? Number(cj.hold_for_payment_orderstatusid)
								: '',
						must_pay_payterms: Array.isArray(cj.must_pay_payterms)
							? cj.must_pay_payterms.map((x: any) => String(x).trim().toUpperCase()).filter((s: string) => s.length > 0)
							: [],
						net_payterms: Array.isArray(cj.net_payterms)
							? cj.net_payterms.map((x: any) => String(x).trim().toUpperCase()).filter((s: string) => s.length > 0)
							: [],
						pgs_sync_erp_status_rank_json:
							Array.isArray(cj.pgs_sync_erp_status_rank) && cj.pgs_sync_erp_status_rank.length > 0
								? JSON.stringify(cj.pgs_sync_erp_status_rank, null, 2)
								: '',
						pgs_sync_erp_skip_when_orderstatusid:
							Array.isArray(cj.pgs_sync_erp_skip_when_orderstatusid)
								? cj.pgs_sync_erp_skip_when_orderstatusid.map((x: any) => Number(x)).filter((n: number) => !isNaN(n))
								: [],
						shipment_invoice_worker_enabled: !!cj.shipment_invoice_worker_enabled,
						worker_stage2_enabled: !!cj.worker_stage2_enabled,
						worker_stage3_enabled: !!cj.worker_stage3_enabled,
						worker_auto_attach_card: cj.worker_auto_attach_card === false ? false : true,
						worker_max_per_run: cj.worker_max_per_run != null && String(cj.worker_max_per_run) !== '' ? Math.max(1, parseInt(String(cj.worker_max_per_run), 10) || 100) : 100,
						worker_stage2_sleep_ms: cj.worker_stage2_sleep_ms != null && String(cj.worker_stage2_sleep_ms) !== '' ? Math.max(0, parseInt(String(cj.worker_stage2_sleep_ms), 10) || 500) : 500,
						worker_stage3_min_ship_age_days: cj.worker_stage3_min_ship_age_days != null && String(cj.worker_stage3_min_ship_age_days) !== '' ? Math.max(0, parseInt(String(cj.worker_stage3_min_ship_age_days), 10) || 0) : 0,
						payment_usage: {
							apply_customer_ar_on_invoice: !!(puDb?.apply_customer_ar_on_invoice ?? cj.apply_customer_ar_on_invoice),
							ar_auto_apply_types_text: arTypesText,
							allow_manual_open_payment_application:
								(puDb?.allow_manual_open_payment_application ?? cj.allow_manual_open_payment_application) === false
									? false
									: true,
							credit_branch_scope:
								(puDb?.credit_branch_scope ?? cj.credit_branch_scope) === 'debtor' ? 'debtor' : 'branch',
							credit_max_auto_apply_amount:
								(puDb?.credit_max_auto_apply_amount ?? cj.credit_max_auto_apply_amount) != null &&
								String(puDb?.credit_max_auto_apply_amount ?? cj.credit_max_auto_apply_amount).trim() !== ''
									? String(puDb?.credit_max_auto_apply_amount ?? cj.credit_max_auto_apply_amount)
									: ''
						}
					}
				});
				this.loadPgsKeywordRules(cj);
				if (config.integration_type === 'PGS') {
					this.loadUniquePgsStatuses();
				}
				this.loading = false;
			},
			error: (error) => {
				this.globalSearchService.showNotification('Error loading configuration', 'danger', 'bottom', 'right');
				this.loading = false;
			}
		});
	}

	private updateIntegrationValidators(): void {
		const integrationType = this.integrationType;
		const apiBaseUrl = this.configForm.get('config_json.api.baseUrl');
		const pgsBaseUrl = this.configForm.get('config_json.pgs.baseUrl');
		const pgsTokenUrl = this.configForm.get('config_json.pgs.tokenUrl');
		const pgsUserId = this.configForm.get('config_json.pgs.userId');
		const pgsPasswd = this.configForm.get('config_json.pgs.passwd');
		const ediPartnerId = this.configForm.get('config_json.edi.tradingPartnerId');
		const sftpHost = this.configForm.get('config_json.sftp.host');
		const sftpUser = this.configForm.get('config_json.sftp.username');
		const sftpPass = this.configForm.get('config_json.sftp.password');

		apiBaseUrl?.clearValidators();
		pgsBaseUrl?.clearValidators();
		pgsTokenUrl?.clearValidators();
		pgsUserId?.clearValidators();
		pgsPasswd?.clearValidators();
		ediPartnerId?.clearValidators();
		sftpHost?.clearValidators();
		sftpUser?.clearValidators();
		sftpPass?.clearValidators();

		if (integrationType === 'API') {
			apiBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
		}

		if (integrationType === 'PGS') {
			pgsBaseUrl?.setValidators([Validators.required, this.urlValidator()]);
			pgsTokenUrl?.setValidators([this.urlValidator()]);
			pgsUserId?.setValidators([Validators.required]);
			pgsPasswd?.setValidators([Validators.required]);
		}

		if (integrationType === 'EDI') {
			ediPartnerId?.setValidators([Validators.required]);
		}

		if (integrationType === 'SFTP' || integrationType === 'FTP') {
			sftpHost?.setValidators([Validators.required]);
			sftpUser?.setValidators([Validators.required]);
			sftpPass?.setValidators([Validators.required]);
		}

		apiBaseUrl?.updateValueAndValidity();
		pgsBaseUrl?.updateValueAndValidity();
		pgsTokenUrl?.updateValueAndValidity();
		pgsUserId?.updateValueAndValidity();
		pgsPasswd?.updateValueAndValidity();
		ediPartnerId?.updateValueAndValidity();
		sftpHost?.updateValueAndValidity();
		sftpUser?.updateValueAndValidity();
		sftpPass?.updateValueAndValidity();
	}

	private urlValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (!value) {
				return null;
			}
			try {
				const parsed = new URL(value);
				if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
					return { url: true };
				}
				return null;
			} catch {
				return { url: true };
			}
		};
	}

	private portValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const value = control.value;
			if (value === null || value === undefined || value === '') {
				return null;
			}
			const port = Number(value);
			if (!Number.isInteger(port) || port < 1 || port > 65535) {
				return { port: true };
			}
			return null;
		};
	}

	saveConfig(): void {
		if (this.configForm.invalid) {
			this.globalSearchService.showNotification('Please fill in all required fields', 'warning', 'bottom', 'right');
			return;
		}

		const formValue = this.configForm.value;
		if (this.config?.enabled && !formValue.enabled) {
			const confirmed = window.confirm('Disable 3PL integration? This will stop exports and imports.');
			if (!confirmed) {
				return;
			}
		}

		this.saving = true;
		const cj = { ...formValue.config_json };
		if (cj.allowed_statuses !== undefined) {
			cj.allowed_statuses = this.normalizeOrderStatusIds(cj.allowed_statuses);
		}
		if (cj.blocked_statuses !== undefined) {
			cj.blocked_statuses = this.normalizeOrderStatusIds(cj.blocked_statuses);
		}
		if (cj.shipped_statuses !== undefined) {
			cj.shipped_statuses = this.normalizeOrderStatusIds(cj.shipped_statuses);
		}
		const toStatusId = (v: any): number | null => { if (v === undefined || v === null || v === '') return null; const n = parseInt(String(v), 10); return isNaN(n) ? null : n; };
		if (cj.status_on_export_success !== undefined) { const v = toStatusId(cj.status_on_export_success); cj.status_on_export_success = v; }
		if (cj.status_on_shipment_received !== undefined) { const v = toStatusId(cj.status_on_shipment_received); cj.status_on_shipment_received = v; }
		if (cj.status_on_tracking_in_transit !== undefined) { const v = toStatusId(cj.status_on_tracking_in_transit); cj.status_on_tracking_in_transit = v; }
		if (cj.status_on_tracking_delivered !== undefined) { const v = toStatusId(cj.status_on_tracking_delivered); cj.status_on_tracking_delivered = v; }
		if (cj.status_on_invoice_failed !== undefined) { const v = toStatusId(cj.status_on_invoice_failed); cj.status_on_invoice_failed = v; }
		if (cj.status_on_shipment_approved !== undefined) { const v = toStatusId(cj.status_on_shipment_approved); cj.status_on_shipment_approved = v; }
		const parseCsvStrings = (s: string): string[] =>
			(s || '').split(/[\s,]+/).map((x: string) => x.trim()).filter((v: string) => v.length > 0);
		if (Array.isArray(cj.sku_categories)) { /* keep */ } else if (cj.sku_categories !== undefined) { cj.sku_categories = parseCsvStrings(String(cj.sku_categories)); }
		if (Array.isArray(cj.sku_lines)) { /* keep */ } else if (cj.sku_lines !== undefined) { cj.sku_lines = parseCsvStrings(String(cj.sku_lines)); }
				if (Array.isArray(cj.sku_manufacturers)) {
			cj.sku_manufacturers = cj.sku_manufacturers.map((x: any) => Number(x)).filter((n: number) => !isNaN(n));
		} else if (cj.sku_manufacturers !== undefined) {
			cj.sku_manufacturers = parseCsvStrings(String(cj.sku_manufacturers)).map((x: string) => parseInt(x, 10)).filter((n: number) => !isNaN(n));
		}
		if (cj.skip_gl_for_3pl_inventory !== undefined) {
			cj.skip_gl_for_3pl_inventory = !!cj.skip_gl_for_3pl_inventory;
		}
		if (cj.auto_invoice_on_shipment !== undefined) {
			cj.auto_invoice_on_shipment = !!cj.auto_invoice_on_shipment;
		}
		if (cj.email_customer_on_3pl_invoice !== undefined) {
			cj.email_customer_on_3pl_invoice = !!cj.email_customer_on_3pl_invoice;
		}
		if (cj.pgs && (cj.pgs as any).auto_credit_on_receipt !== undefined) {
			(cj.pgs as any).auto_credit_on_receipt = !!(cj.pgs as any).auto_credit_on_receipt;
		}
		const puForm = (formValue.config_json as { payment_usage?: Record<string, unknown> }).payment_usage;
		delete (cj as { payment_usage?: unknown }).payment_usage;
		delete (cj as { apply_customer_ar_on_invoice?: unknown }).apply_customer_ar_on_invoice;
		delete (cj as { ar_auto_apply_types?: unknown }).ar_auto_apply_types;
		delete (cj as { allow_manual_open_payment_application?: unknown }).allow_manual_open_payment_application;
		delete (cj as { credit_branch_scope?: unknown }).credit_branch_scope;
		delete (cj as { credit_max_auto_apply_amount?: unknown }).credit_max_auto_apply_amount;
		const arTypesText = String(puForm?.ar_auto_apply_types_text ?? '').trim();
		const arParsed = arTypesText
			? arTypesText.split(/[\s,]+/).map((x) => parseInt(x.trim(), 10)).filter((n) => n === 11 || n === 12)
			: [];
		const scopeStr = String(puForm?.credit_branch_scope ?? 'branch').trim();
		const maxArRaw = puForm?.credit_max_auto_apply_amount;
		const paymentUsage: Record<string, unknown> = {
			apply_customer_ar_on_invoice: !!puForm?.apply_customer_ar_on_invoice,
			ar_auto_apply_types: arParsed.length ? arParsed : [11],
			allow_manual_open_payment_application: puForm?.allow_manual_open_payment_application !== false,
			credit_branch_scope: scopeStr === 'debtor' ? 'debtor' : 'branch'
		};
		if (maxArRaw !== undefined && maxArRaw !== null && String(maxArRaw).trim() !== '') {
			const mx = parseFloat(String(maxArRaw));
			if (!isNaN(mx) && mx > 0) {
				paymentUsage.credit_max_auto_apply_amount = mx;
			}
		}
		(cj as { payment_usage: Record<string, unknown> }).payment_usage = paymentUsage;
		if (cj.split_partial_backorder_enabled !== undefined) {
			cj.split_partial_backorder_enabled = !!cj.split_partial_backorder_enabled;
		}
		if (cj.split_uninvoiced_open_lines_enabled !== undefined) {
			cj.split_uninvoiced_open_lines_enabled = !!cj.split_uninvoiced_open_lines_enabled;
		}
		if (cj.auto_split_partial_before_export !== undefined) {
			cj.auto_split_partial_before_export = !!cj.auto_split_partial_before_export;
		}
		if (cj.skip_not_fillable_orders !== undefined) {
			cj.skip_not_fillable_orders = !!cj.skip_not_fillable_orders;
		}
		if (cj.not_fillable_hold_minutes !== undefined) {
			const n = parseInt(String(cj.not_fillable_hold_minutes), 10);
			cj.not_fillable_hold_minutes = !isNaN(n) && n >= 1 ? n : 60;
		}
		if (cj.queue_backorder_for_tpl3pl !== undefined) {
			cj.queue_backorder_for_tpl3pl = !!cj.queue_backorder_for_tpl3pl;
		}
		if (cj.backorder_orderstatusid !== undefined) {
			const v = toStatusId(cj.backorder_orderstatusid);
			if (v == null) {
				delete cj.backorder_orderstatusid;
			} else {
				cj.backorder_orderstatusid = v;
			}
		}
		if (cj.hold_for_payment_orderstatusid !== undefined) {
			const v = toStatusId(cj.hold_for_payment_orderstatusid);
			if (v == null) {
				delete cj.hold_for_payment_orderstatusid;
			} else {
				cj.hold_for_payment_orderstatusid = v;
			}
		}
		const normalizePaytermCodes = (raw: any): string[] => {
			if (!Array.isArray(raw)) {
				return [];
			}
			const seen: { [k: string]: true } = {};
			const out: string[] = [];
			for (const v of raw) {
				const s = String(v ?? '').trim().toUpperCase();
				if (s !== '' && !seen[s]) {
					seen[s] = true;
					out.push(s);
				}
			}
			return out;
		};
		if (cj.must_pay_payterms !== undefined) {
			const arr = normalizePaytermCodes(cj.must_pay_payterms);
			if (arr.length > 0) {
				cj.must_pay_payterms = arr;
			} else {
				delete cj.must_pay_payterms;
			}
		}
		if (cj.net_payterms !== undefined) {
			const arr = normalizePaytermCodes(cj.net_payterms);
			if (arr.length > 0) {
				cj.net_payterms = arr;
			} else {
				delete cj.net_payterms;
			}
		}
		const keywordRules: Array<{match: string[], order_status_id: number}> = [];
		
		for (const item of this.uniquePgsStatuses) {
			const id = parseInt(String(item.mappedStatusId), 10);
			if (!isNaN(id) && id > 0) {
				keywordRules.push({ match: [item.status.toLowerCase()], order_status_id: id });
			}
		}
		
		if (keywordRules.length > 0) {
			cj.pgs_sync_erp_keyword_status_ids = keywordRules;
		} else {
			delete cj.pgs_sync_erp_keyword_status_ids;
		}
		const pgsRankStr = String((cj as any).pgs_sync_erp_status_rank_json ?? '').trim();
		delete (cj as any).pgs_sync_erp_status_rank_json;
		if (pgsRankStr) {
			try {
				const parsed = JSON.parse(pgsRankStr);
				if (Array.isArray(parsed)) {
					const rank = parsed.map((x: any) => parseInt(String(x), 10)).filter((n: number) => !isNaN(n) && n > 0);
					if (rank.length > 0) {
						cj.pgs_sync_erp_status_rank = rank;
					}
				} else {
					this.globalSearchService.showNotification('PGS status rank must be a JSON array', 'warning', 'bottom', 'right');
					this.saving = false;
					return;
				}
			} catch {
				this.globalSearchService.showNotification('Invalid JSON in PGS status rank', 'warning', 'bottom', 'right');
				this.saving = false;
				return;
			}
		}
		if (Array.isArray(cj.pgs_sync_erp_skip_when_orderstatusid)) {
			cj.pgs_sync_erp_skip_when_orderstatusid = cj.pgs_sync_erp_skip_when_orderstatusid
				.map((x: any) => Number(x))
				.filter((n: number) => !isNaN(n) && n > 0);
			if (cj.pgs_sync_erp_skip_when_orderstatusid.length === 0) {
				delete cj.pgs_sync_erp_skip_when_orderstatusid;
			}
		}
		if (cj.pgs && (cj.pgs as any).inventoryMaxResults !== undefined && (cj.pgs as any).inventoryMaxResults !== null && (cj.pgs as any).inventoryMaxResults !== '') {
			const n = parseInt(String((cj.pgs as any).inventoryMaxResults), 10);
			(cj.pgs as any).inventoryMaxResults = !isNaN(n) ? Math.min(2000, Math.max(1, n)) : 500;
		}
		if (cj.pgs) {
			const pgs = cj.pgs as Record<string, unknown>;
			const invLoc = String(pgs.inventoryLoccode ?? '').trim();
			if (invLoc) {
				pgs.inventoryLoccode = invLoc;
			} else {
				delete pgs.inventoryLoccode;
			}
			const wmStr = String(pgs.warehouseMapJson ?? '').trim();
			delete pgs.warehouseMapJson;
			if (!wmStr) {
				delete pgs.warehouseMap;
			} else {
				try {
					const parsed = JSON.parse(wmStr);
					if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
						this.globalSearchService.showNotification('PGS warehouse map must be a JSON object', 'warning', 'bottom', 'right');
						this.saving = false;
						return;
					}
					const out: Record<string, string> = {};
					for (const k of Object.keys(parsed)) {
						const v = String((parsed as Record<string, unknown>)[k] ?? '').trim();
						if (v !== '') {
							out[String(k).trim()] = v;
						}
					}
					if (Object.keys(out).length > 0) {
						pgs.warehouseMap = out;
					} else {
						delete pgs.warehouseMap;
					}
				} catch {
					this.globalSearchService.showNotification('Invalid JSON in PGS warehouse map', 'warning', 'bottom', 'right');
					this.saving = false;
					return;
				}
			}
		}
		if (cj.pgs && (cj.pgs as any).lob) {
			const lobForm = (cj.pgs as any).lob;
			const lobDefault = String(lobForm.default ?? '').trim();
			const parseLobObjectJson = (raw: string, label: string): Record<string, string> | null => {
				const s = String(raw ?? '').trim();
				if (!s) {
					return {};
				}
				try {
					const parsed = JSON.parse(s);
					if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
						const out: Record<string, string> = {};
						for (const k of Object.keys(parsed)) {
							out[String(k)] = String((parsed as Record<string, unknown>)[k] ?? '').trim();
						}
						return out;
					}
					this.globalSearchService.showNotification(`${label} must be a JSON object`, 'warning', 'bottom', 'right');
					return null;
				} catch {
					this.globalSearchService.showNotification(`Invalid JSON in ${label}`, 'warning', 'bottom', 'right');
					return null;
				}
			};
			const idMapParsed = parseLobObjectJson(String(lobForm.byManufacturerIdJson ?? ''), 'LOB manufacturer ID map');
			if (idMapParsed === null) {
				this.saving = false;
				return;
			}
			const nameMapParsed = parseLobObjectJson(String(lobForm.byManufacturerNameJson ?? ''), 'LOB manufacturer name map');
			if (nameMapParsed === null) {
				this.saving = false;
				return;
			}
			const idMap: Record<string, string> = {};
			for (const k of Object.keys(idMapParsed)) {
				if (idMapParsed[k] !== '') {
					idMap[k] = idMapParsed[k];
				}
			}
			const byMap: Record<string, string> = {};
			for (const k of Object.keys(nameMapParsed)) {
				if (nameMapParsed[k] !== '') {
					byMap[k] = nameMapParsed[k];
				}
			}
			if (lobDefault || Object.keys(byMap).length > 0 || Object.keys(idMap).length > 0) {
				(cj.pgs as any).lob = {};
				if (lobDefault) {
					(cj.pgs as any).lob.default = lobDefault;
				}
				if (Object.keys(idMap).length > 0) {
					(cj.pgs as any).lob.byManufacturerId = idMap;
				}
				if (Object.keys(byMap).length > 0) {
					(cj.pgs as any).lob.byManufacturerName = byMap;
				}
			} else {
				delete (cj.pgs as any).lob;
			}
		}
		if (cj.shipment_invoice_worker_enabled !== undefined) { cj.shipment_invoice_worker_enabled = !!cj.shipment_invoice_worker_enabled; }
		if (cj.worker_stage2_enabled !== undefined) { cj.worker_stage2_enabled = !!cj.worker_stage2_enabled; }
		if (cj.worker_stage3_enabled !== undefined) { cj.worker_stage3_enabled = !!cj.worker_stage3_enabled; }
		if (cj.worker_auto_attach_card !== undefined) { cj.worker_auto_attach_card = cj.worker_auto_attach_card !== false; }
		if (cj.worker_max_per_run !== undefined) { const n = parseInt(String(cj.worker_max_per_run), 10); cj.worker_max_per_run = !isNaN(n) && n >= 1 ? n : 100; }
		if (cj.worker_stage2_sleep_ms !== undefined) { const n = parseInt(String(cj.worker_stage2_sleep_ms), 10); cj.worker_stage2_sleep_ms = !isNaN(n) && n >= 0 ? n : 500; }
		if (cj.worker_stage3_min_ship_age_days !== undefined) { const n = parseInt(String(cj.worker_stage3_min_ship_age_days), 10); cj.worker_stage3_min_ship_age_days = !isNaN(n) && n >= 0 ? n : 0; }
		delete (cj as any).syncSchedule;
		delete (cj as any).sync_schedule;
		const config: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: cj
		};

		this.tpl3plService.saveConfig(config).subscribe({
			next: (result: any) => {
				this.saving = false;
				if (result && result.success !== false) {
					this.globalSearchService.showNotification('Configuration saved successfully', 'success', 'bottom', 'right');
					this.loadConfig();
					this.tpl3plService.notifyConfigSaved();
				} else {
					const msg = (result && result.message) ? result.message : 'Save failed';
					this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				const msg = error?.error?.message || error?.message || 'Error saving configuration';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				this.saving = false;
			}
		});
	}

	testConnection(): void {
		this.testing = true;
		this.testResult = null;

		const formValue = this.configForm.value;
		const testConfig: Tpl3plConfig = {
			integration_type: formValue.integration_type,
			enabled: formValue.enabled,
			test_mode: formValue.test_mode,
			warehouse_code: formValue.warehouse_code,
			config_json: formValue.config_json
		};

		this.tpl3plService.testConnection(testConfig).subscribe({
			next: (result) => {
				this.testResult = result;
				this.testing = false;
				if (result.success) {
					this.globalSearchService.showNotification('Connection test successful', 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification('Connection test failed: ' + result.message, 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.testResult = { success: false, message: 'Connection test error' };
				this.testing = false;
				this.globalSearchService.showNotification('Connection test error', 'danger', 'bottom', 'right');
			}
		});
	}

	get integrationType(): string {
		return this.configForm.get('integration_type')?.value || 'API';
	}

	get apiForm(): UntypedFormGroup {
		return this.configForm.get('config_json.api') as UntypedFormGroup;
	}

	get ediForm(): UntypedFormGroup {
		return this.configForm.get('config_json.edi') as UntypedFormGroup;
	}

	get sftpForm(): UntypedFormGroup {
		return this.configForm.get('config_json.sftp') as UntypedFormGroup;
	}

	get pgsForm(): UntypedFormGroup {
		return this.configForm.get('config_json.pgs') as UntypedFormGroup;
	}
}
